import { Link } from 'react-router-dom'
import { useReveal } from '../components/marketing/motion'
import { usePageMeta } from '../lib/usePageMeta'

// Public REST reference — the /public endpoints that power the Compare page and the homepage hero card
// (apps/api/src/routes/public.ts). ONE RULE SINCE 07.10 (owner; navo24-web #208): data with a
// computation needs a key and counts as a call, an empty answer is free -- `/public/schedules`,
// `/public/reliability/lanes`, `/public/reliability/leaderboard`, `/public/port-congestion`
// (lib/keyed-public.ts). They kept their paths, so the `/public` prefix is history, not a promise.
// Reference lists (carriers, ports search, covered) need no key and are never counted, carriers
// deliberately so: it is the vocabulary the `?carrier=` filter accepts, needed before a key.
//
// CARDS IS ABSENT ON PURPOSE, from this page and from the published OpenAPI, and it is not an
// oversight to be helpfully corrected. `/public/schedules/cards` also requires a key now (it is the
// second entry in KEYED_PUBLIC), but the owner's position is that it is not available to customers at
// the moment: its data is not being refreshed, and we say so rather than advertise it. Documenting an
// endpoint we describe as unavailable is the contradiction, not the omission. The site's own Compare
// page still calls it through the edge proxy, which is internal use and not a published surface.
// When it reopens to customers, document it in the same change.
//
// TWO limits are enforced on the OPEN reference endpoints, and both belong on the page. A burst limit
// of 120 requests per 20 seconds per address (call-rate-limit.ts, mounted at public.ts:113) and a daily total of 2,000 per
// address (index.ts, PUBLIC_IP_DAILY_CAP). This comment previously asserted the burst limiter was
// mounted on /v1 and /distance-time "not here", which is contradicted by the mount one grep away, and
// the page was corrected to match the comment rather than the code. A customer who paces to 2,000 a day
// and sends them in a burst still gets a 429 nothing documented.
const API_BASE = 'https://schedulesmcp-api.fly.dev'

interface Endpoint {
  method: 'GET' | 'POST'
  path: string
  summary: string
  curl: string
  sample?: string
  /** This endpoint's error codes, by HTTP status, as its code returns them. */
  errors?: { status: number; codes: string }[]
}

// Authenticated /v1 surface (API key). Base + endpoints verified against apps/api/src/routes.
const V1_BASE = 'https://api.schedulesmcp.com/v1'
// Only the endpoints a customer actually uses. The /v1 schedules, best-sailings, reliability and
// port-congestion routes are ours: our own MCP server and web app call them, every one has a
// documented /public counterpart, and the reference at navo24.com lists none of them. Owner's ruling
// (2026-09-07): what we use only ourselves is not shown to clients.
const V1_ENDPOINTS: Endpoint[] = [
  {
    method: 'POST',
    path: '/v1/webhooks',
    summary:
      'Register an https endpoint for schedule-change and alert events. The signing secret is returned once.',
    curl: `curl -X POST "${V1_BASE}/webhooks" -H "Authorization: Bearer smcp_YOURKEY" -H "Content-Type: application/json" -d '{"url":"https://you.example/hook"}'`,
  },
]

function EndpointCard({ e }: { e: Endpoint }) {
  return (
    <section className="bg-surface-0 border border-border rounded-2xl p-6">
      <h2 className="font-mono text-base mb-2">
        <span className="text-signal font-bold mr-2">{e.method}</span>
        <span className="text-[var(--text-primary)]">{e.path}</span>
      </h2>
      <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4">{e.summary}</p>
      <pre className="bg-[var(--surface-1)] border border-border rounded-xl p-4 overflow-x-auto text-sm font-mono text-[var(--text-secondary)] leading-relaxed">
        {e.curl}
      </pre>
      {e.sample && (
        <>
          <p className="text-xs font-mono uppercase tracking-wide text-[var(--text-muted)] mt-4 mb-2">
            Sample response (trimmed)
          </p>
          <pre className="bg-[var(--surface-1)] border border-border rounded-xl p-4 overflow-x-auto text-xs font-mono text-[var(--text-secondary)] leading-relaxed">
            {e.sample}
          </pre>
        </>
      )}
      {e.errors && (
        <table className="mt-4 text-xs font-mono text-[var(--text-secondary)]">
          <tbody>
            {e.errors.map((r) => (
              <tr key={r.status}>
                <td className="pr-4 align-top text-[var(--text-muted)]">{r.status}</td>
                <td className="leading-relaxed">{r.codes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

const ENDPOINTS: Endpoint[] = [
  {
    method: 'GET',
    path: '/public/schedules?origin=&destination=&from=&weeks=&carrier=&transshipment=&routings=',
    summary:
      'Upcoming published sailings on a lane (UN/LOCODE origin/destination), soonest departure first. Where a sailing reached us from is not part of the customer contract and is not in the response. What is: cutoff_status, which says whether a cut-off is published, not published by that carrier, or not held by us because the sailing came from another line selling the same ship. Cut-offs are set per carrier, so we never lend one carrier’s deadline to another. Times: every departure, arrival, leg time and cut-off is the local time of its own port, in the digits the line publishes on its own site, for every line alike; we convert nothing. The +00:00 on the end is only there so the value parses as a date-time and does not mean UTC: 2026-09-29T05:00:00+00:00 on a Shanghai departure is 05:00 in Shanghai. Read the digits and do not convert them to your zone. departure_precision and arrival_precision say whether the line gave a time (minute) or only a date (day, and the 00:00 is ours); null means not classified yet. Two ports keep two clocks, so arrival minus departure is not the voyage length: use transit_days. Our own bookkeeping times (as_of, last_changed_at, retrieved_at, changed_since) are real UTC. Start from date - specify the date in format yyyy-mm-dd. Must be current date or future date. Weeks - how many weeks forward from the start date, one of 4, 8, 12, defaults to 4. A value we cannot honour returns 400. The window end is exclusive. The answer is paged: "limit" sets the page size, 100 by default and 500 at most, and "next" is the next request already built for you. Follow it while it is there and stop when it is null, never on an empty or a short page: with the transshipment filter on, a stretch of the window can be read and yield nothing while the rest is still ahead, and a page can end a few sailings before the limit so that a sailing a line files through two of its own feeds is served once on the whole walk. Each sailing carries "sailing_id", the key to store it under: it identifies the sailing across requests and survives a revision, so a line refiling a moved departure changes the dates and not the id. It is not eternal, and the one case that ends it is where we hold one sailing as two rows and later merge them, because the row that loses is removed. Alongside it "last_changed_at" says when we last saw the carrier move that sailing, which is the value changed_since filters on; null there means we have never seen it move, so in a delta that is how you tell a new sailing from one that shifted. Each sailing also names the ship: vessel_imo is the hull number, which is what identifies a ship because a name does not, and vessel carries a few particulars of that same hull (teu, teu_estimated, flag, year_built, length_m) or null where we do not hold her. The number and the particulars always describe one hull: where we hold no row for the hull a carrier filed, vessel_imo still stands and vessel is null rather than another ship’s figures. Read teu together with teu_estimated, which is true when the capacity was derived from the ship’s deadweight instead of published for her, about one capacity in five. flag is a country name as the register spells it, not a two-letter code. The answer also carries "latest_departure_held", the last departure anyone has filed on this lane whatever window you asked for. Read it when the far half of your window came back empty: if it sits before the window end, nobody has published that far ahead yet, which is a different thing from there being no service. Carriers file to very different horizons, from a fortnight to seven months. Changed since - only sailings that are new to us since that moment, or whose published departure or arrival we have seen the carrier move since it. Pass back the as_of value from your last successful read and you get the delta rather than the whole window. How much that saves depends on how often you ask: on a busy lane a daily poll reads roughly a fifth of the window, a weekly poll about half, because a week is long enough for much of the lane to have moved. Read the limits before you build on it. What is watched is those two dates and nothing else, so a cut-off moving, a name being corrected or a routing changing does not put a sailing in a delta. With routings=all, a change in the other routings the line publishes for a departure does (see Routings); last_changed_at still moves only with the two dates. A sailing that moves keeps its sailing_id, so a delta names the row you already hold rather than a replacement for it. Removals are not reported, so a delta refreshes and adds but cannot prune. Accepts YYYY-MM-DD, read as midnight UTC, or a full instant such as 2026-09-11T06:00:00Z, from 1970 onwards. A time in the future is refused rather than answered with an empty list. Carrier - one or more SCAC codes, comma-separated, defaulting to all; a code finds every sailing of that line even where it was filed under another of its codes, and the response echoes the code you asked for. The first page carries "carriers": one entry per line in the whole answer, every page counted, with carrier_code, carrier_name and count, most sailings first; the counts add up to the sailings you collect by following "next" to the end. Without a carrier filter it lists every line with a sailing plus every line the live search asked or held back; with one, exactly the lines you asked for. Each entry has "status": ok (sailings), no_service (confirmed none in this window: the line answered for it, or its data reaches past the start date), timeout or failed (asked now, no answer), not_attempted (no confirmation for this window), not_supported (no source for this line); and "checked_at", when the line last confirmed the lane; and "latest_departure", that line’s last departure within the window you requested; null when the line has no sailings in the window (see status); at the window’s end it means “at least up to here”. Reading the line’s horizon from it: more than a sailing interval (about a week) before the window’s end with status ok – the line publishes only up to that date; within about a week of the end – at least up to here, ask a wider window to see further; null with no_service – the line does not sail this lane in your window. "complete" is true when no listed zero is a gap; false: for at least one line the answer may be incomplete (that line’s status in "carriers" says which), or the live search did not run (it never runs with transshipment) or was held back by our limits. "error" (timeout, failed, paused) stays for existing clients. Later pages answer null for "carriers" and "complete". Transshipment - "true" gives sailings with transshipment, "false" gives direct schedules only, and omitting it gives all sailings. Each sailing carries is_transshipment, transship_via, transit_days and route_legs, and via_port_names names the hubs. is_transshipment is three-valued: true, false, or null where we do not hold enough of the routing to say. route_legs is the routing as the carrier filed it, one entry per leg, each with its ports, its mode, and the ship where a ship is named. routing_status is known when is_transshipment is true or false, unknown when it is null. With the filter on, lane capacity and suspected blank sailings are not computed, because both are facts about the lane rather than about the filter. Routings - "all" gives every routing the line published for a departure: each departure is followed by its other routings as sailings of their own with is_alternative true, the main one has is_alternative false, and an alternative shares its departure’s sailing_id, ship and cut-offs. Left out, the answer is as before and is_alternative is absent. limit and carriers count departures, count counts the sailings in the page, next keeps the flag, and the price is that of a normal search. The transshipment filter judges every routing on its own legs: a departure is served when any of its routings fits, and only the routings that fit are served, so it can appear with is_alternative true alone. Each routing of a departure is served once.',
    curl: `curl "${API_BASE}/public/schedules?origin=CNSHA&destination=NLRTM" -H "Authorization: Bearer smcp_YOURKEY"`,
    sample: `{
  "ok": true,
  "data": {
    "origin": "CNSHA",
    "destination": "NLRTM",
    "origin_name": "Shanghai",
    "destination_name": "Rotterdam",
    "from": "2026-07-14T00:00:00.000Z",
    "to": "2026-08-11T00:00:00.000Z",
    "weeks": 4,
    "count": 2,
    "carriers": [
      { "carrier_code": "MAEU", "carrier_name": "Maersk", "count": 2, "latest_departure": "2026-08-04T00:00:00", "status": "ok", "checked_at": "2026-09-28T04:37:12.000Z" },
      { "carrier_code": "MSCU", "carrier_name": "MSC", "count": 0, "latest_departure": null, "status": "timeout", "checked_at": "2026-09-28T09:20:14.882Z", "error": "timeout" }
    ],
    "complete": false,
    "changed_since": null,
    "latest_departure_held": "2026-10-31T00:00:00.000Z",
    "as_of": "2026-07-11T09:20:14.882Z",
    "next": null,
    "sailings": [
      {
        "sailing_id": "2f6b1e94-7c1a-4a6e-9a6f-3d8a5c0b1e22",
        "carrier_code": "MAEU",
        "carrier_name": "Maersk",
        "service_name": "AE10",
        "vessel_name": "MADRID MAERSK",
        "voyage_number": "528W",
        "vessel_imo": "9778791",
        "vessel": {
          "teu": 20568,
          "teu_estimated": false,
          "flag": "Denmark",
          "year_built": 2017,
          "length_m": 399
        },
        "published_departure": "2026-07-14T04:00:00+00:00",
        "published_arrival": "2026-08-13T22:00:00+00:00",
        "cy_cutoff": "2026-07-11T12:00:00+00:00",
        "doc_cutoff": null,
        "vgm_cutoff": null,
        "cutoffs": [
          { "type": "FCO", "at": "2026-07-11T12:00:00+00:00", "label": "FCL delivery" },
          { "type": "X-BKS", "at": "2026-07-09T12:00:00+00:00", "label": "Booking stop" }
        ],
        "cutoff_status": "published",
        "last_changed_at": "2026-07-09T11:42:03.000Z",
        "retrieved_at": "2026-09-28T04:37:12.000Z"
      },
      {
        "sailing_id": "8c40a7d1-55e2-4f0b-9d33-b1a6e2c47f05",
        "carrier_code": "HDMU",
        "carrier_name": "HMM",
        "service_name": "FE3",
        "vessel_name": "HMM STOCKHOLM",
        "voyage_number": "OSKT0019W",
        "vessel_imo": "9868352",
        "vessel": {
          "teu": 23964,
          "teu_estimated": false,
          "flag": "Korea",
          "year_built": 2020,
          "length_m": 400
        },
        "published_departure": "2026-08-07T00:00:00+00:00",
        "published_arrival": "2026-10-22T00:00:00+00:00",
        "cy_cutoff": null,
        "doc_cutoff": null,
        "vgm_cutoff": null,
        "cutoffs": null,
        "cutoff_status": "not_held",
        "last_changed_at": null,
        "retrieved_at": "2026-09-27T22:51:40.000Z"
      }
    ]
  }
}`,
    // Checked against routes/public.ts, services/lane-schedules.ts and the key middleware. No 404:
    // an empty lane is a 200 with no sailings, and free.
    errors: [
      {
        status: 400,
        codes:
          'MISSING_PARAMETER, INVALID_DATE_FORMAT, DATE_IN_PAST, INVALID_WEEKS, INVALID_SCAC, INVALID_TRANSSHIPMENT, INVALID_CHANGED_SINCE, CHANGED_SINCE_IN_FUTURE, INVALID_LIMIT, INVALID_CURSOR; error.parameter names the input where there is one',
      },
      { status: 401, codes: 'UNAUTHORIZED: no key, or an unknown, revoked or expired one' },
      { status: 403, codes: 'FORBIDDEN (no Schedules access), access_expired' },
      { status: 404, codes: 'never: an empty lane is a 200 with no sailings, and free' },
      {
        status: 429,
        codes: 'quota_exceeded (monthly quota or daily key limit), RATE_LIMITED (key pace)',
      },
      { status: 500, codes: 'DB_ERROR' },
      { status: 503, codes: 'SERVICE_UNAVAILABLE: retry after Retry-After' },
    ],
  },
  {
    method: 'GET',
    path: '/public/schedules/covered',
    summary:
      'Lanes that currently have upcoming cached sailings, busiest first, each with its port names alongside the UN/LOCODEs.',
    curl: `curl "${API_BASE}/public/schedules/covered"`,
  },
  {
    method: 'GET',
    path: '/public/reliability/lanes?origin=&destination=',
    summary:
      'Carriers serving a lane ranked by observed 90-day on-time reliability, with average / p90 / max delay, berth precision in minutes, sample size, and a provisional flag on thin samples (fewer than 20 observed sailings).',
    curl: `curl "${API_BASE}/public/reliability/lanes?origin=CNSHA&destination=THLCH" -H "Authorization: Bearer smcp_YOURKEY"`,
    sample: `{
  "ok": true,
  "data": {
    "origin": "CNSHA",
    "destination": "THLCH",
    "origin_name": "Shanghai",
    "destination_name": "Laem Chabang",
    "min_obs": 20,
    "carriers": [
      {
        "carrier_code": "ONEY",
        "carrier_name": "Ocean Network Express",
        "on_time_pct": "11.76",
        "avg_delay_hours": "170.4",
        "p90_delay_hours": "312.0",
        "observations": 27,
        "berth_precision_p50_min": 14,
        "delay_basis": "first_declared",
        "provisional": false
      }
    ]
  }
}`,
  },
  {
    method: 'GET',
    path: '/public/reliability/covered',
    summary:
      'Lanes that currently have reliability observations, most-observed first, each with its port names alongside the UN/LOCODEs.',
    curl: `curl "${API_BASE}/public/reliability/covered"`,
  },
  {
    method: 'GET',
    path: '/public/reliability/leaderboard?limit=',
    summary:
      'Carrier-level on-time league table across all observed lanes. Only carriers with 20+ observations get a rank; the rest follow with rank null and provisional true. Optional limit is an integer from 1 to 100. The response includes total before limiting, ranked_total, delay statistics, and a nullable schedule-churn volatility block.',
    curl: `curl "${API_BASE}/public/reliability/leaderboard?limit=10" -H "Authorization: Bearer smcp_YOURKEY"`,
    sample: `{
  "ok": true,
  "data": {
    "min_obs": 20,
    "total": 65,
    "ranked_total": 39,
    "carriers": [{
      "rank": 1,
      "ranked": true,
      "carrier_code": "YMLU",
      "carrier_name": "Yang Ming",
      "on_time_pct": 70.55,
      "avg_delay_hours": 17.01,
      "p90_delay_hours": 66.05,
      "max_delay_hours": 368.5,
      "observations": 343,
      "provisional": false,
      "volatility": {
        "revisions_per_sailing": 3.448,
        "mean_abs_etd_hours": 64.4,
        "p90_abs_etd_hours": 144,
        "late_etd_share": 0.391,
        "churn_hours_per_sailing": 222.1,
        "etd_revisions": 12243,
        "eta_revisions": 6736,
        "revised_sailings": 6544,
        "active_sailings": 3551,
        "level": "high"
      }
    }]
  }
}`,
  },
  {
    method: 'GET',
    path: '/public/ports/search?q=',
    summary:
      'Port typeahead: resolve a port name (or partial, or UN/LOCODE) to its canonical UN/LOCODE with country and coordinates.',
    curl: `curl "${API_BASE}/public/ports/search?q=laem"`,
  },
  {
    method: 'GET',
    path: '/public/port-congestion?port=',
    summary:
      'Congestion signal for a port (level, expected added wait in days, confidence, sources). Omit ?port= for the congested-ports leaderboard.',
    curl: `curl "${API_BASE}/public/port-congestion?port=NLRTM" -H "Authorization: Bearer smcp_YOURKEY"`,
  },
]

export function DocsPage() {
  usePageMeta(
    'API docs: sailing schedule, reliability & port congestion endpoints · SchedulesMCP',
    'REST endpoints for live ocean sailing schedules, carrier on-time reliability and port congestion. Data endpoints take an API key and count 1 call per answer with data; an empty answer is free. Carrier and port reference lists are open and free. Copy-paste curl examples and real JSON responses.'
  )
  useReveal('main')
  return (
    <main className="max-w-5xl mx-auto px-6 pt-12 pb-10">
      <p
        className="m-0 font-mono text-xs uppercase text-accent text-center"
        style={{ letterSpacing: '0.14em' }}
      >
        Developer docs
      </p>
      <h1
        className="font-display text-4xl mb-3 mt-3 text-center"
        style={{ fontWeight: 210, letterSpacing: '-0.03em' }}
      >
        API documentation
      </h1>
      <p className="text-[var(--text-secondary)] mb-4 max-w-2xl text-center mx-auto">
        Every lane search on this site runs on these REST endpoints, the same live engine your API
        key queries. Call them straight from curl, a spreadsheet, or an AI agent.
      </p>
      <ul className="text-sm text-[var(--text-secondary)] mb-10 space-y-1">
        <li>
          <span className="font-mono text-[var(--text-primary)]">Base URL</span> —{' '}
          <code className="font-mono text-signal">{API_BASE}</code>
        </li>
        <li>
          Data endpoints (schedules, reliability, port congestion) need{' '}
          <code className="font-mono">Authorization: Bearer smcp_YOURKEY</code>: each answer that
          carries data counts as 1 call, an empty answer and any refusal or error are free · a pace
          of 120 requests per 20 seconds per key (60 on a test key)
        </li>
        <li>
          Reference lists (covered lanes, carriers, port search) need no key and are never counted ·
          120 requests per 20 seconds per IP, and 2,000 per day, resetting at midnight UTC · a
          refusal carries Retry-After, and RateLimit-Policy says which of the two you met
        </li>
        <li>
          Machine-readable spec:{' '}
          <a href="/openapi.json" className="text-signal underline underline-offset-2">
            /openapi.json
          </a>{' '}
          · agent reference:{' '}
          <a href="/llms-full.txt" className="text-signal underline underline-offset-2">
            /llms-full.txt
          </a>{' '}
          · AI clients:{' '}
          <Link to="/mcp" className="text-signal underline underline-offset-2">
            MCP server
          </Link>
        </li>
      </ul>

      <div className="space-y-8">
        {ENDPOINTS.map((e) => (
          <EndpointCard key={e.path} e={e} />
        ))}
      </div>

      {/* Keyed surface — webhooks only; the /v1 data routes are internal. */}
      <div className="mt-16">
        <p
          className="m-0 font-mono text-xs uppercase text-accent text-center"
          style={{ letterSpacing: '0.14em' }}
        >
          Authenticated API
        </p>
        <h2
          className="font-display text-3xl mb-3 mt-2 text-center"
          style={{ fontWeight: 210, letterSpacing: '-0.03em' }}
        >
          Webhooks
        </h2>
        <ul className="text-sm text-[var(--text-secondary)] mb-8 space-y-1 max-w-2xl mx-auto">
          <li>
            <span className="font-mono text-[var(--text-primary)]">Base URL</span> —{' '}
            <code className="font-mono text-signal">{V1_BASE}</code>
          </li>
          <li>
            <span className="font-mono text-[var(--text-primary)]">Auth</span> —{' '}
            <code className="font-mono">Authorization: Bearer smcp_YOURKEY</code> on every request
          </li>
          <li>
            Keys from your{' '}
            <Link to="/dashboard" className="text-signal underline underline-offset-2">
              dashboard
            </Link>{' '}
            (a Schedules subscription is required). The data endpoints above need one: send it as{' '}
            <code className="font-mono">Authorization: Bearer</code> and each answer with data
            counts against the quota agreed for your account; an empty answer is free. There are no
            plans or tiers here: that quota, access staying valid, and a pace of 120 requests per 20
            seconds (60 on a test key) are the whole of it. The endpoints keep their paths, so an
            existing integration adds a header and changes nothing else.
          </li>
          <li>
            Every successful request to <code className="font-mono">/v1/webhooks</code> and its
            sub-paths counts as 1 call; refusals and errors are free, and the event deliveries we
            send to your endpoint are free. Registering, updating and testing an endpoint need
            SchedulesMCP connected to your account; listing, reading and deleting need only a valid
            key.
          </li>
          <li>
            Every response is <code className="font-mono">{'{ ok, data }'}</code> or{' '}
            <code className="font-mono">{'{ ok, error: { code, message } }'}</code>; branch on{' '}
            <code className="font-mono">code</code>.
          </li>
        </ul>
        <div className="space-y-8">
          {V1_ENDPOINTS.map((e) => (
            <EndpointCard key={e.path} e={e} />
          ))}
        </div>
        <div className="mt-8 bg-surface-0 border border-border rounded-2xl p-6">
          <h2 className="font-mono text-base mb-2 text-[var(--text-primary)]">
            Webhook signatures
          </h2>
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4">
            Every delivery carries an HMAC signature. Verify it before you trust the payload. The
            secret is the <code className="font-mono">whsec_</code> value returned when you register
            the endpoint.
          </p>
          <pre className="bg-[var(--surface-1)] border border-border rounded-xl p-4 overflow-x-auto text-sm font-mono text-[var(--text-secondary)] leading-relaxed">
            {'X-SchedulesMCP-Signature: sha256=HMAC_SHA256(whsec_secret, raw_body)'}
          </pre>
        </div>
      </div>

      <div className="mt-12 border-t border-border pt-8">
        <h2 className="font-display font-light text-xl mb-3 tracking-[-0.015em]">Need more?</h2>
        <p className="text-sm text-[var(--text-secondary)] max-w-2xl mb-5">
          The authenticated <span className="font-mono">/v1</span> API adds full lane coverage,
          reliability history, and alerts, with your own API and MCP keys. It carries the same 120
          requests per 20 seconds; what a key changes is the allowance, which is set on the key
          itself rather than on your address. Both limits on the open reference endpoints are per IP
          and intended for interactive and agent use, not bulk scraping.
        </p>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
          <Link
            to="/pricing"
            className="bg-signal text-on-accent font-bold px-5 py-2 rounded-lg no-underline hover:opacity-90"
          >
            Get your keys
          </Link>
          <Link to="/compare" className="text-sm text-signal no-underline hover:underline">
            Or try the live lane search →
          </Link>
        </div>
      </div>
    </main>
  )
}
